import type { AssistedDraft } from "@/lib/assistedOrder";
import type { MajorNote } from "@/lib/majorNotes";
import { getCartItems, setCartItems } from "@/lib/sessionUtils";
import { priceWizardSelection, type WizardCartPackaging, type WizardPriced, type WizardCatalogProduct, type WizardBoxProduct } from "../../supabase/functions/_shared/wizardCartPricing";

export { priceWizardSelection, type WizardCartPackaging, type WizardPriced, type WizardCatalogProduct, type WizardBoxProduct };

/** Customer wizard → order page hand-off (never the staff `assistedOrderDraft` key). */
export const WIZARD_ORDER_KEY = "wizardOrderDraft";
export const WIZARD_ORDER_ROUTE = "/wizard-order";
export type WizardSource = "perfume_self" | "perfume_gift" | "corporate";

export interface WizardOrderPick {
  product_id: string; name: string; code: string | null; slug: string | null; image: string | null;
  story: string; major_notes?: MajorNote[]; theme?: string; category?: string; role?: string;
}
export interface WizardOrderDraft {
  v: 1; run_id: string; source: WizardSource; language: "en" | "ar"; return_path: string;
  recipient_name: string; greeting: string | null;
  picks: WizardOrderPick[]; wizard_context: Record<string, unknown>;
}

/** Builds the customer draft from the same draft the staff booking uses, plus catalogue codes/slugs. */
export function toWizardOrderDraft(d: AssistedDraft, o: { source: WizardSource; run_id: string; language: "en" | "ar"; return_path: string; recipient_name?: string; identity: Record<string, { code?: string | null; slug?: string | null }> }): WizardOrderDraft {
  const card = (d.wizard_context as { card?: { greeting?: string } }).card;
  return {
    v: 1, run_id: o.run_id, source: o.source, language: o.language, return_path: o.return_path,
    recipient_name: (o.recipient_name || "").trim(), greeting: card?.greeting?.trim() || null,
    picks: d.picks.map((p) => ({ product_id: p.product_id, name: p.name, image: p.image, story: p.story, major_notes: p.major_notes, theme: p.theme, category: p.category, role: p.role,
      code: o.identity[p.product_id]?.code || null, slug: o.identity[p.product_id]?.slug || null })),
    wizard_context: { ...d.wizard_context, source_wizard: d.source_wizard, wizard_source: o.source },
  };
}

export function isWizardOrderDraft(x: unknown): x is WizardOrderDraft {
  const d = x as WizardOrderDraft;
  return !!d && d.v === 1 && typeof d.run_id === "string" && !!d.run_id && ["perfume_self", "perfume_gift", "corporate"].includes(d.source)
    && Array.isArray(d.picks) && d.picks.length >= 1 && d.picks.length <= 3 && d.picks.every((p) => typeof p?.product_id === "string" && !!p.product_id && typeof p.name === "string")
    && new Set(d.picks.map((p) => p.product_id)).size === d.picks.length;
}
export const saveWizardOrderDraft = (d: WizardOrderDraft) => sessionStorage.setItem(WIZARD_ORDER_KEY, JSON.stringify(d));
export const loadWizardOrderDraft = (): WizardOrderDraft | null => {
  try { const d = JSON.parse(sessionStorage.getItem(WIZARD_ORDER_KEY) || "null"); return isWizardOrderDraft(d) ? d : null; } catch { return null; }
};

/** One cart line per wizard session: re-continuing replaces it instead of adding a duplicate. */
export const wizardCartItemId = (d: Pick<WizardOrderDraft, "source" | "run_id">) => `wizard-${d.source}-${d.run_id}`;

/** Packaging offered for this draft: a box only for exactly three different perfumes. */
export const allowedPackagings = (d: WizardOrderDraft): WizardCartPackaging[] => (d.picks.length === 3 ? ["box", "single_100", "single_50"] : ["single_100", "single_50"]);
export const productIdsFor = (d: WizardOrderDraft, packaging: WizardCartPackaging, singleId: string | null) =>
  packaging === "box" ? d.picks.map((p) => p.product_id) : singleId && d.picks.some((p) => p.product_id === singleId) ? [singleId] : [];

export interface WizardCartOptions {
  packaging: WizardCartPackaging; productIds: string[]; priced: WizardPriced;
  recipientName: string; country: string; photoUrl?: string | null; originalPhotoUrl?: string | null;
}

/** Cart line in the existing personalized cart shape; checkout re-prices it on the server from `wizard`. */
export function buildWizardCartItem(d: WizardOrderDraft, o: WizardCartOptions) {
  const box = o.packaging === "box";
  if (box && d.picks.length !== 3) throw new Error("A Signature Box needs exactly three perfumes");
  const chosen = o.productIds.map((id) => d.picks.find((p) => p.product_id === id)).filter((p): p is WizardOrderPick => !!p);
  if (chosen.length !== o.productIds.length || !chosen.length) throw new Error("Selected perfume is not part of this wizard result");
  const perfumes = chosen.map((p) => {
    const server = o.priced.products.find((x) => x.id === p.product_id);
    return { id: p.product_id, name: server?.name || p.name, code: server?.code || p.code, perfumeCode: server?.code || p.code, type: "wizard", narration: p.story, image: p.image, majorNotes: p.major_notes || [], theme: p.theme || "", role: p.role || "" };
  });
  return {
    id: wizardCartItemId(d), type: box ? "box" : "single", packageName: o.priced.label,
    packageName_ar: box ? "صندوق التوقيع (3 × 50 مل)" : `${perfumes[0].name} (${o.priced.size_ml} مل)`,
    perfume: box ? undefined : perfumes[0], perfumes: box ? perfumes : undefined,
    greetingMessage: d.greeting || undefined, photoUrl: box ? o.photoUrl || undefined : undefined, originalPhotoUrl: box ? o.originalPhotoUrl || undefined : undefined,
    recipientName: o.recipientName.trim(), country: o.country, quantity: 1, price: o.priced.unit_price,
    isWizardItem: true,
    wizard: { source: d.source, run_id: d.run_id, packaging: o.packaging, product_ids: o.productIds, size_ml: o.priced.size_ml, language: d.language, context: d.wizard_context, draft: d },
  };
}

/** Replace the same wizard line in place (keeps its address/selection), otherwise append. */
export function upsertCartItem<T extends { id: string }>(cart: T[], item: T & { shippingAddressId?: string }): T[] {
  const i = cart.findIndex((c) => c.id === item.id);
  if (i < 0) return [...cart, item];
  const prev = cart[i] as T & { shippingAddressId?: string };
  const next = [...cart]; next[i] = { ...item, shippingAddressId: item.shippingAddressId || prev.shippingAddressId }; return next;
}
export function addWizardItemToCart(item: ReturnType<typeof buildWizardCartItem>) { setCartItems(upsertCartItem(getCartItems(), item)); }

/** Country already fixed by other cart lines (multi-recipient carts share one country). */
export const lockedCartCountry = (cart: { id: string; country?: string }[], ownId: string) => cart.find((c) => c.id !== ownId && c.country)?.country || null;

/** Back from sign-in/address steps: the wizard order page when the latest personalized line is a wizard line. */
export function personalizationBackPath(cart: { isWizardItem?: boolean; isCollectionItem?: boolean; wizard?: { draft?: unknown } }[] = getCartItems()) {
  const last = [...cart].reverse().find((c) => !c.isCollectionItem);
  if (last?.isWizardItem && isWizardOrderDraft(last.wizard?.draft)) { saveWizardOrderDraft(last.wizard!.draft as WizardOrderDraft); return WIZARD_ORDER_ROUTE; }
  return "/personalization";
}

/** Edit a wizard cart line: reopen its order page instead of the main quiz. Returns false for other lines. */
export function reopenWizardItem(item: { isWizardItem?: boolean; wizard?: { draft?: unknown } }): boolean {
  if (!item.isWizardItem || !isWizardOrderDraft(item.wizard?.draft)) return false;
  saveWizardOrderDraft(item.wizard!.draft as WizardOrderDraft);
  return true;
}
